import * as React from "react";
import { useState, useEffect } from "react";
import CollapsibleGroup from "@/components/atomics/collapsible-group";
import TabSwitcher from "@/components/atomics/tab-switcher";
import WhitelistEligible from "../white-list/eligible";
import WhitelistManager from "../white-list/whitelist-manager";
import MotionModal from "@/components/atomics/motion-modal";
import WhitelistAddSuccessMessage from "@/components/atomics/whitelist-add-success-message";
import WhitelistRemoveSuccessMessage from "@/components/atomics/whitelist-remove-success-message";
import { WalletListItem } from "@/services/backend";
import Guide from "@/components/molecules/guide/guide";
import { whiteListGuideData } from "@/data/guide";
import InfoIcon from "@/components/atomics/info-icon";
import { useDispatch, useSelector } from "react-redux";
import { actions, RootState } from "@/state/reducer";
import { whitelistTracker } from "@/components/utils/trackWhitelist";

interface UseOwnerWhitelistProps {
  isLoading: boolean;
  whitelistItems: WalletListItem[];
  requestItems: WalletListItem[];
  listedItems: WalletListItem[];
  totalRequestsCount: number;
  totalListedCount: number;
  manageWhitelist: (wallets: string[], isAdd: boolean) => void;
  showAddSuccessModal: boolean;
  showRemoveSuccessModal: boolean;
  lastProcessedWallets: string[];
  closeAddSuccessModal: () => void;
  closeRemoveSuccessModal: () => void;
  loadMoreRequests: (offset: number) => Promise<number>;
  loadMoreListed: (offset: number) => Promise<number>;
  showLessRequests: () => void;
  showLessListed: () => void;
  refreshWhitelistData: (page?: number) => Promise<void>;
  fetchWhitelistData: (
    whitelisted?: boolean,
    limit?: number,
    offset?: number
  ) => Promise<WalletListItem[]>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  requestsCanLoadMore: boolean;
  listedCanLoadMore: boolean;
  requestsCanShowLess: boolean;
  listedCanShowLess: boolean;
}

interface WhitelistSectionProps {
  collection: string;
  ownerWhitelist: UseOwnerWhitelistProps;
}

export default function WhitelistSection({
  collection,
  ownerWhitelist,
}: WhitelistSectionProps) {
  const [activeTab, setActiveTab] = useState<
    "eligible" | "requests" | "listed"
  >("eligible");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestsSelectedWallets, setRequestsSelectedWallets] = useState<
    Set<string>
  >(new Set());
  const [listedSelectedWallets, setListedSelectedWallets] = useState<
    Set<string>
  >(new Set());
  const [eligibleItems, setEligibleItems] = useState<WalletListItem[]>([]);
  const { whitelistTrigger } = useSelector((state: RootState) => state);
  const [showWhitelistGuideModal, setShowWhitelistGuideModal] =
    useState<boolean>(false);
  const dispatch = useDispatch();

  React.useMemo(() => {
    whitelistTracker(collection);
    setInterval(() => {
      dispatch(actions.whitelistTrigger(String(Math.random())));
    }, 300000);
  }, []);

  const {
    requestItems,
    listedItems,
    totalRequestsCount,
    totalListedCount,
    requestsCanLoadMore,
    listedCanLoadMore,
    requestsCanShowLess,
    listedCanShowLess,
    manageWhitelist,
    isLoading,
    showAddSuccessModal,
    showRemoveSuccessModal,
    lastProcessedWallets,
    closeAddSuccessModal,
    closeRemoveSuccessModal,
    loadMoreRequests,
    loadMoreListed,
    showLessRequests,
    showLessListed,
    searchTerm,
    setSearchTerm,
  } = ownerWhitelist;

  // Clear selections only after successful operations, not during search
  useEffect(() => {
    if (showAddSuccessModal) {
      setRequestsSelectedWallets(new Set());
    }
  }, [showAddSuccessModal]);

  useEffect(() => {
    showLessRequests();
  }, [whitelistTrigger]);

  useEffect(() => {
    if (showRemoveSuccessModal) {
      setListedSelectedWallets(new Set());
    }
  }, [showRemoveSuccessModal]);

  // Note: Selection validation removed - selections should persist across all search operations
  // They will only be cleared by explicit user actions or successful whitelist operations

  const handleEligibleItemsChange = (items: WalletListItem[]) => {
    setEligibleItems(items);
  };

  const handleTabChange = (tab: "eligible" | "requests" | "listed") => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSyncWithRequests = (eligibleWallets: string[]) => {
    const matchingWallets = requestItems
      .filter(item => eligibleWallets.includes(item.wallet))
      .map(item => item.wallet);

    setRequestsSelectedWallets(new Set(matchingWallets));
    handleTabChange("requests");
  };

  const handleOpenWhitelistGuideModal = () => {
    setShowWhitelistGuideModal(true);
  };

  const handleCloseWhitelistGuideModal = () => {
    setShowWhitelistGuideModal(false);
  };

  return (
    <div className="relative">
      <CollapsibleGroup
        isEditView={false}
        editTitle={"WHITELIST"}
        viewTitle={"WHITELIST"}
        extendedViewClassName="w-full px-[30px] py-[15px] text-[30px] bg-black text-white"
        collapsedViewClassName="w-full px-[30px] py-[15px] text-[30px] bg-black text-white"
      >
        <InfoIcon
          className="absolute top-[23px] left-[216px] size-8 hover:cursor-pointer"
          onClick={handleOpenWhitelistGuideModal}
        />

        <TabSwitcher
          active={activeTab}
          onChange={handleTabChange}
          counts={{
            eligible: eligibleItems.length,
            requests: totalRequestsCount,
            listed: totalListedCount,
          }}
        />

        {activeTab === "eligible" && (
          <WhitelistEligible
            whiteListItems={eligibleItems}
            activeTab={activeTab}
            onSyncWithRequests={handleSyncWithRequests}
            onEligibleItemsChange={handleEligibleItemsChange}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}

        {activeTab === "requests" && (
          <WhitelistManager
            whiteListItems={requestItems}
            activeTab={activeTab}
            mode="add"
            onWhitelistAction={manageWhitelist}
            preSelectedWallets={requestsSelectedWallets}
            onSelectionChange={setRequestsSelectedWallets}
            loadMoreData={loadMoreRequests}
            showLessData={showLessRequests}
            canLoadMore={requestsCanLoadMore}
            canShowLess={requestsCanShowLess}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSearchChange={setSearchTerm}
          />
        )}

        {activeTab === "listed" && (
          <WhitelistManager
            whiteListItems={listedItems}
            activeTab={activeTab}
            mode="remove"
            onWhitelistAction={manageWhitelist}
            preSelectedWallets={listedSelectedWallets}
            onSelectionChange={setListedSelectedWallets}
            showTotalCount={true}
            loadMoreData={loadMoreListed}
            showLessData={showLessListed}
            canLoadMore={listedCanLoadMore}
            canShowLess={listedCanShowLess}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSearchChange={setSearchTerm}
          />
        )}
      </CollapsibleGroup>
      <MotionModal isOpen={showWhitelistGuideModal}>
        <Guide
          guideData={whiteListGuideData}
          onClose={handleCloseWhitelistGuideModal}
        />
      </MotionModal>

      <MotionModal isOpen={showAddSuccessModal}>
        <WhitelistAddSuccessMessage
          className="w-full bg-black"
          walletAddress={lastProcessedWallets[0] || ""}
          walletCount={lastProcessedWallets.length}
          onClose={closeAddSuccessModal}
          onClick={closeAddSuccessModal}
        />
      </MotionModal>

      <MotionModal isOpen={showRemoveSuccessModal}>
        <WhitelistRemoveSuccessMessage
          className="w-full bg-black"
          walletAddress={lastProcessedWallets[0] || ""}
          walletCount={lastProcessedWallets.length}
          onClose={closeRemoveSuccessModal}
          onClick={closeRemoveSuccessModal}
        />
      </MotionModal>
    </div>
  );
}
